import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";

import Button from "@mui/material/Button";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useMemo, useState } from "react";
import {
  INITIAL_PATIENT_OTHER_CONSENT,
  NewPatientForm,
} from "../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { MEDIUM_LIST } from "../../../../../pages/app/provider/patient/patient-list/patient-chart-new/assessment/PatientAssementForms/assessment-constant";
import { DOCUMENT_FORMAT_MAPPER } from "../../../../../pages/app/provider/patient/patient-list/patient-chart-new/document/document-constant";
import { useGetAllScreenerType } from "../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  convertToBase64,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import CustomDatePicker from "../../../../CustomDatePicker";
import CustomSelect from "../../../../CustomSelect";
import CustomTimePicker from "../../../../CustomTimePicker";
import Flex from "../../../../Flex";
import Input from "../../../../Input";
import MaskMultiSelectScroll from "@/components/MaskMultiSelectScroll";
import OutlineCard from "../../../../OutlineCard";
import Text from "../../../../Text";
import { VisuallyHiddenInput } from "../../../../VisuallyHiddenInput";
import CustomTitle from "../../../CustomTitle";
import StyledButtonV2 from "../../../StyledButtonV2";
interface PrivacyAndOtherConsentProps {
  formik: FormikProps<NewPatientForm>;
}

const PrivacyAndOtherConsent = ({ formik }: PrivacyAndOtherConsentProps) => {
  const { values, handleBlur, handleChange, setFieldValue } = formik;
  const [fileNames, setFileNames] = useState<string[]>([]);

  const [searchString, setSearchString] = useDebounce<string>("");
  const [screenerPagination, setScreenerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: screenerTypeList, isFetching: screenerFetching } =
    useGetAllScreenerType({
      pagination: screenerPagination,
      xTENANTID: getXtenantId(),
      searchString: searchString,
    });

  const screenerOptions = useMemo(() => {
    const fetched =
      screenerTypeList?.data?.content?.map((item: any) => ({
        value: item,
        label: replaceUnderScoreAddSpace(item),
      })) ?? [];
    const selected = (formik.values.assessments?.assessment ?? []) as {
      value: any;
      label: string;
    }[];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o.value === s.value)
    );
    return [...missing, ...fetched];
  }, [screenerTypeList?.data?.content, formik.values.assessments?.assessment]);

  const handleDateValue = (date: Date, type: "date" | "time") => {
    if (type === "time" && formik.values?.assessments?.recordedDate) {
      const preDate = new Date(formik.values.assessments?.recordedDate);
      preDate?.setHours(date.getHours(), date.getMinutes(), date.getSeconds());
      formik.setFieldValue("assessments.recordedDate", preDate?.toISOString());
    } else
      formik.setFieldValue("assessments.recordedDate", date?.toISOString());
  };
  return (
    <>
      <Box id={"Consent"}>
        <CustomTitle mb={1}>Consent</CustomTitle>
        <Grid container gap={1.5} direction={"column"}>
          <OutlineCard>
            <Flex flexDirection={"column"} p={0.8}>
              <Flex gap={5} alignItems={"center"}>
                <FormControlLabel
                  label={<Text>Consent to Email</Text>}
                  control={<Checkbox />}
                  checked={values.emailConsent}
                  onChange={(_, value) => setFieldValue("emailConsent", value)}
                />
                <FormControlLabel
                  label={<Text>Consent to Call</Text>}
                  control={<Checkbox />}
                  checked={values.callConsent}
                  onChange={(_, value) => setFieldValue("callConsent", value)}
                />
                <FormControlLabel
                  label={<Text>Consent to Message</Text>}
                  control={<Checkbox />}
                  checked={values.messageConsent}
                  onChange={(_, value) =>
                    setFieldValue("messageConsent", value)
                  }
                />
              </Flex>
            </Flex>
          </OutlineCard>
        </Grid>
      </Box>
      {/* new consent form — only for new patient, hidden in edit flow */}
      {!(formik.values as any)?.uuid && (
        <Box mt={1} border={"1px solid #E0E0E0"} borderRadius={2} p={2}>
          {formik.values?.patientConsentEntities?.map((_: any, ind: number) => (
            <Box key={ind} id={"otherConsent"}>
              <Grid item container spacing={3} mb={2}>
                <Grid item xs={3}>
                  <Input
                    label="Enter Name"
                    textFieldProps={{
                      name: `patientConsentEntities[${ind}].name`,
                      value: values.patientConsentEntities[ind]?.name,
                      onChange: handleChange,
                      onBlur: handleBlur,
                      // placeholder: "Enter Name",
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <CustomDatePicker
                    label="Consent Date"
                    value={values.patientConsentEntities[ind]?.documentDate}
                    onChange={(value) => {
                      formik.setFieldValue(
                        `patientConsentEntities[${ind}].documentDate`,
                        value
                      );
                    }}
                    // placeholder="Choose Date"
                  />
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    component="label"
                    variant={
                      values?.patientConsentEntities[ind]?.consentDocument
                        ? "text"
                        : "outlined"
                    }
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    sx={{ height: "34px" }}
                  >
                    {fileNames?.[ind] || "Upload File"}
                    <VisuallyHiddenInput
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={async (event) => {
                        const files = event?.target?.files;

                        if (files) {
                          const fileName = files[0].name;

                          const copiedfiles = [...fileNames];
                          copiedfiles[ind] = fileName;
                          setFileNames(copiedfiles);

                          const base64File = await convertToBase64(files[0]);
                          formik.setFieldValue(
                            `patientConsentEntities[${ind}].document`,
                            base64File
                          );
                        } else
                          formik.setFieldValue(
                            `patientConsentEntities[${ind}].document`,
                            undefined
                          );

                        const curFile = files?.item(0);
                        formik.setFieldValue("file", curFile);
                        formik.setFieldValue(
                          `patientConsentEntities[${ind}].assigned`,
                          true
                        );
                        if (curFile?.type) {
                          formik.setFieldValue(
                            `patientConsentEntities[${ind}].documentFormat`,
                            DOCUMENT_FORMAT_MAPPER?.[curFile.type] ?? "OTHER"
                          );
                        }
                        formik.setFieldValue(
                          `patientConsentEntities[${ind}].uploadedAt`,
                          new Date().toISOString()
                        );
                        formik.setFieldValue(
                          `patientConsentEntities[${ind}].patientDocumentTags`,
                          "CONSENT_FORM"
                        );
                        formik.setFieldValue(
                          `patientConsentEntities[${ind}].documentDescription`,
                          values.patientConsentEntities[ind]?.name
                        );
                      }}
                    />
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Flex width={"100%"} justifyContent={"center"}>
                    <StyledButtonV2
                      onClick={() => {
                        const isOnlyRow =
                          (formik.values.patientConsentEntities?.length ??
                            0) === 1;
                        if (isOnlyRow) {
                          // Only one row exists → clear content in place instead of removing.
                          // Reset all UI-bound fields explicitly since the Input uses `name`
                          // (not `consentName` from INITIAL_PATIENT_OTHER_CONSENT).
                          formik.setFieldValue(
                            `patientConsentEntities[${ind}]`,
                            {
                              ...INITIAL_PATIENT_OTHER_CONSENT,
                              name: "",
                              documentDate: undefined,
                              document: undefined,
                              consentDocument: undefined,
                              assigned: false,
                              documentFormat: undefined,
                              uploadedAt: undefined,
                              patientDocumentTags: undefined,
                              documentDescription: undefined,
                            }
                          );
                          setFileNames((prev) => {
                            const next = [...prev];
                            next[ind] = "";
                            return next;
                          });
                        } else {
                          // More than one row → remove this row from the array
                          // (any index, including 0, can be removed).
                          formik.setFieldValue(
                            "patientConsentEntities",
                            formik.values.patientConsentEntities?.toSpliced(
                              ind,
                              1
                            )
                          );
                          setFileNames((prev) => prev.toSpliced(ind, 1));
                        }
                      }}
                      variant="text"
                      color="primary"
                      label={
                        (formik.values.patientConsentEntities?.length ?? 0) ===
                        1
                          ? "Clear"
                          : "Remove"
                      }
                      sIcon={<HighlightOffOutlinedIcon />}
                    />
                  </Flex>
                </Grid>
              </Grid>
            </Box>
          ))}
          {!!formik.values?.patientConsentEntities?.length &&
            formik.values?.patientConsentEntities?.length < 9 && (
              <StyledButtonV2
                onClick={() => {
                  formik.setFieldValue("patientConsentEntities", [
                    ...(formik.values.patientConsentEntities ?? []),
                    INITIAL_PATIENT_OTHER_CONSENT,
                  ]);
                }}
                shouldAddIcon
                variant="text"
                label={
                  formik.values?.patientConsentEntities?.length
                    ? "Add Another consent"
                    : "Add Consent"
                }
              />
            )}
        </Box>
      )}

      {/* note : Patient Assessment Forms is only for new patient */}
      {!(formik.values as any)?.uuid && (
        <Box>
          <CustomTitle mb={1}>Assessment Forms</CustomTitle>
          <Grid container gap={1.5} direction={"column"}>
            <OutlineCard>
              <Grid item container spacing={2.5}>
                <Grid item xs={4}>
                  <MaskMultiSelectScroll
                    limitTags={1}
                    // placeholder="Select Screening Forms"
                    onSearch={(value) => {
                      setSearchString(value);
                      setScreenerPagination((p) => ({ ...p, page: 0 }));
                    }}
                    options={screenerOptions}
                    value={formik.values.assessments?.assessment}
                    onChange={(values) => {
                      formik.setFieldValue("assessments.assessment", values);
                    }}
                    error={
                      (formik.touched?.assessments as any)?.assessment &&
                      (formik.errors?.assessments as any)?.assessment
                    }
                    pagination={screenerPagination}
                    setPagination={setScreenerPagination}
                    totalElements={screenerTypeList?.data?.page?.totalElements}
                    loading={screenerFetching}
                  />
                </Grid>
                {/* Date */}
                <Grid item xs={5}>
                  {/* <CustomDatePicker
                    name="Schedule Date"
                    minDate={dayjs(new Date())}
                    label="Schedule Date"
                    value={
                      formik.values?.assessments?.scheduleTime
                        ? dayjs(formik.values?.assessments?.scheduleTime)
                        : null
                    }
                    onChange={(value) =>
                      formik.setFieldValue("assessments.scheduleTime", value)
                    }
                    placeholder="Schedule Date"
                    handleBlur={formik.handleBlur}
                  /> */}
                  <Flex gap={2}>
                    <CustomDatePicker
                      label="Date"
                      name="assessments.recordedDate"
                      value={formik.values?.assessments?.recordedDate}
                      onChange={(val) =>
                        handleDateValue(dayjs(val)?.toDate(), "date")
                      }
                    />
                    <CustomTimePicker
                      label="Time"
                      // placeholder="HH::MM::AM"
                      name="assessments.recordedDate"
                      value={formik.values?.assessments?.recordedDate}
                      onChange={(val) =>
                        handleDateValue(dayjs(val)?.toDate(), "time")
                      }
                    />
                  </Flex>
                </Grid>

                {/* Rendering Provider */}
                <Grid item xs={3}>
                  <CustomSelect
                    name="Medium"
                    label="Medium"
                    required={!!formik.values.assessments?.assessment?.length}
                    value={formik.values.assessments?.reminderMedium}
                    options={MEDIUM_LIST.filter((item) => {
                      if (
                        formik.values.mobileNumber === undefined ||
                        formik.values.mobileNumber === "" ||
                        formik.values.messageConsent === false
                      ) {
                        return item.value !== "SMS";
                      }
                      return true;
                    })}
                    onChange={(val) =>
                      formik.setFieldValue("assessments.reminderMedium", val)
                    }
                    handleBlur={formik.handleBlur}
                    error={
                      (formik.touched?.assessments as any)?.reminderMedium &&
                      (formik.errors?.assessments as any)?.reminderMedium
                    }
                  />
                </Grid>
              </Grid>
            </OutlineCard>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default PrivacyAndOtherConsent;
